import InfoCircleOutlined from "@ant-design/icons/lib/icons/InfoCircleOutlined";
import {
  ApprovalRule,
  RequestorProfile,
  UserApprovalOptions,
} from "@p0security/shared/types/workflow/types";
import {
  Button,
  Checkbox,
  CheckboxOptionType,
  Flex,
  Popover,
  Space,
} from "antd";
import { newApprovalRuleOfType } from "components/Jit/Routing/store/mutations";
import {
  approvalTypeLabels,
  getApprovalIcon,
} from "components/Jit/Routing/views/common";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { useCallback, useContext, useMemo } from "react";
import { CheckboxValueType } from "types/antd";

import { RoutingRuleEditorContext } from "../../store/RoutingRuleEditorContext.tsx";
import { EscalationApproval } from "../EscalationApproval.tsx";
import { IdpGroupInput } from "../IdpGroupInput";
import { OnCallAllow } from "../OnCallAllow.tsx";
import { RequestorProfileInput } from "../RequestorProfileInput";
import { TicketApproval } from "../TicketApproval.tsx";
import { LocalAccessRuleContext } from "./AccessEditor";

type Props = {
  cancelButton?: React.ReactNode;
  onSave: (rule: ApprovalRule) => void;
};
const baseSelectionTypes: ApprovalRule["type"][] = [
  "group",
  "requestor-profile",
  "p0",
  "deny",
  "persistent",
];

const autoApprovalTypes: ApprovalRule["type"][] = ["auto", "escalation"];

const ticketApprovalTypes: ApprovalRule["type"][] = ["ticket"];

export const AccessRuleEditor: React.FC<Props> = ({ cancelButton, onSave }) => {
  const { localRule, setLocalRule, isDirty } = useContext(
    LocalAccessRuleContext
  );

  const { installed } = useContext(RoutingRuleEditorContext);

  const handleSelectAccessType = useCallback(
    (selection: ApprovalRule["type"]) => {
      setLocalRule(newApprovalRuleOfType(selection, installed));
    },
    [installed, setLocalRule]
  );

  const handleRequestorProfileChange = useCallback(
    (requestorProfile: RequestorProfile) => {
      if (localRule?.type !== "requestor-profile") {
        return;
      }

      const newRule =
        requestorProfile.directory === "okta"
          ? // Only showing property override for Okta right now, as Workspace has a more opinionated way of accessing the user's manager.
            { ...localRule, ...requestorProfile }
          : {
              type: "requestor-profile" as const,
              directory: requestorProfile.directory,
            };
      setLocalRule(newRule);
    },
    [setLocalRule, localRule]
  );

  const routingOptions = useMemo(() => {
    if (!localRule) {
      return [];
    }

    if (localRule.type === "deny") {
      return [];
    }

    const options: CheckboxOptionType[] = [];
    options.push({
      value: "requireReason",
      label: "Require reason with request",
    });
    options.push({
      value: "requireDuration",
      label: "Require duration with request",
    });
    options.push({
      value: "requirePreapproval",
      label: "Require preapproval with request",
    });

    if (
      localRule.type === "p0" ||
      localRule.type === "group" ||
      localRule.type === "escalation" ||
      localRule.type === "requestor-profile"
    ) {
      options.push({
        value: "allowOneParty",
        label: "Allow self-approvals",
      });
      options.push({
        value: "breakGlassApprover",
        label: "Approve break-glass access",
      });
    }
    return options;
  }, [localRule]);

  const handleChangeApprovalOptions = useCallback(
    (options: CheckboxValueType[]) => {
      if (!localRule) {
        return;
      }

      if (localRule.type === "deny") {
        return;
      }

      setLocalRule({
        ...localRule,
        options: convertApprovalOptionsArrayToObject(
          options as ApprovalOptionsArray
        ),
      });
    },
    [localRule, setLocalRule]
  );

  const saveRule = useCallback(
    () => localRule && onSave(localRule),
    [localRule, onSave]
  );

  const available = useMemo<PillSelectOption<ApprovalRule["type"]>[]>(() => {
    const keys: ApprovalRule["type"][] = [
      ...baseSelectionTypes,
      ...(installed.auto.length ? autoApprovalTypes : []),
      ...(installed.ticket.length ? ticketApprovalTypes : []),
    ];
    return keys.map((sel) => ({
      icon: getApprovalIcon(sel),
      label: approvalTypeLabels[sel] ?? sel,
      value: sel,
    }));
  }, [installed]);

  return (
    <Space direction="vertical" className="w-full p-2 mt-4 bg-gray-50">
      <Flex align="center" gap={12} justify="space-between" wrap="wrap">
        <PillSelect<ApprovalRule["type"]>
          value={localRule?.type}
          onChange={handleSelectAccessType}
          options={available}
          placeholder="Choose an action"
        />
        {cancelButton}
      </Flex>
      {localRule?.type === "group" && (
        <IdpGroupInput value={localRule} onChange={setLocalRule} />
      )}
      {localRule?.type === "auto" && installed.auto.length > 0 && (
        <OnCallAllow
          value={localRule}
          installed={installed.auto}
          onChange={setLocalRule}
        />
      )}
      {localRule?.type === "escalation" && installed.auto.length > 0 && (
        <EscalationApproval
          value={localRule}
          installed={installed.auto}
          onChange={setLocalRule}
        />
      )}
      {localRule?.type === "requestor-profile" && (
        <RequestorProfileInput
          value={localRule}
          onChange={handleRequestorProfileChange}
        />
      )}
      {localRule?.type === "ticket" && installed.ticket.length > 0 && (
        <TicketApproval
          value={localRule}
          installed={installed.ticket}
          onChange={setLocalRule}
        />
      )}
      {localRule && routingOptions.length > 0 && localRule.type !== "deny" && (
        <div>
          <Checkbox.Group
            value={convertApprovalOptionsObjectToArray(localRule.options)}
            onChange={handleChangeApprovalOptions}
          >
            <div className="flex flex-col">
              {routingOptions.map((option) => (
                <Checkbox key={option.value} value={option.value}>
                  {option.label}{" "}
                  {option.value === "breakGlassApprover" && (
                    <Popover
                      content={
                        <div style={{ maxWidth: 350 }}>
                          Only available for SSH access
                        </div>
                      }
                    >
                      <InfoCircleOutlined className="info" />
                    </Popover>
                  )}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </div>
      )}
      {localRule && (
        <Button type="primary" onClick={saveRule} disabled={!isDirty}>
          Submit
        </Button>
      )}
    </Space>
  );
};

const convertApprovalOptionsObjectToArray = (
  approvalOptions: UserApprovalOptions | undefined
) => {
  const options = [];
  if (approvalOptions?.allowOneParty) {
    options.push("allowOneParty");
  }
  if (approvalOptions?.requireReason) {
    options.push("requireReason");
  }
  if (approvalOptions?.requireDuration) {
    options.push("requireDuration");
  }
  if (approvalOptions?.requirePreapproval) {
    options.push("requirePreapproval");
  }
  if (approvalOptions?.breakGlassApprover) {
    options.push("breakGlassApprover");
  }
  return options;
};

type ApprovalOptionsArray = (
  | "allowOneParty"
  | "breakGlassApprover"
  | "requireDuration"
  | "requirePreapproval"
  | "requireReason"
)[];

const convertApprovalOptionsArrayToObject = (options: ApprovalOptionsArray) => {
  return Object.fromEntries(options.map((o) => [o, true]));
};
